/**
 * Drawer Component Script
 *
 * This script enables a sliding drawer component that appears when a trigger button is clicked.
 * The drawer can be closed by clicking on the close button or the overlay.
 *
 * The simpliest structure to start with:
 * 
<button class="js-drawer-cta" for="drawer-{{ block.id }}">
    open drawer
</button>
<drawer-simple class="drawer-simple" role="dialog" id="drawer-{{ block.id }}">
    <div class="drawer-overlay js-drawer-close"></div>    
    <div class="drawer-inner">
        <div class="drawer-header">
          Title
          <span role="button" class="button-close button-close--icon drawer-close-btn js-drawer-close">
            {% render 'icon-v1', name: 'close' %}
          </span>
        </div>
        <div class="drawer-content">
            
        </div>
    </div>
</drawer-simple>
 *
 */
class DrawerSimple extends HTMLElement {
  constructor() {
    super();
  }

  /**
   * Called when the element is added to the document. Initializes the drawer,
   * moves it to the body to prevent stacking context issues, and binds global events.
   */
  connectedCallback() {
    // Required to trigger .inited CSS rules that set display: flex on .drawer-inner
    this.classList.add('inited');
    this.setAttribute('tabindex', '-1');

    // Move to body to avoid z-index/overflow issues.
    if (this.parentElement && this.parentElement !== document.body && !this.classList.contains('drawer-no-move')) {
      document.body.append(this);
    }

    // Bind close events inside the drawer
    this.addEventListener('click', (e) => {
      if (!e.target.closest('.js-drawer-close')) return;
      e.closedLayer = this;
      this.close();
    });

    // Escape belongs to FocusLayer, which dismisses only the topmost layer.
  }

  /**
   * Sets the active element that triggered the drawer to return focus to it on close.
   * @param {HTMLElement} element - The triggering element
   */
  setActiveElement(element) {
    this.activeElement = element;
  }

  /**
   * Opens the drawer and locks the body scroll.
   * @param {HTMLElement|null} triggeredBy - Optional element that triggered the opening
   * @param {Object|null} layerOptions - FocusLayer overrides, for a drawer that must
   *   not seal the page (e.g. the size guide anchored above a card's size sheet)
   */
  open(triggeredBy = null, layerOptions = null) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('opened')) return;

    // Both flushes are load-bearing. The first gives the transition a start value —
    // a drawer fetched and opened in one task has had no style pass yet. The second
    // makes the `.opened` styles current: the popup variant is `visibility: hidden`
    // until then, and an element inside `visibility: hidden` cannot take focus.
    this.getBoundingClientRect();
    this.classList.add('opened');
    this.getBoundingClientRect();

    FocusLayer.push(this, {
      modal: true,
      contain: true,
      lockScroll: true,
      trigger: this.activeElement || null,
      initialFocus: this.querySelector('.drawer-close-btn'),
      onDismiss: () => this.close(),
      ...layerOptions,
    });
  }

  /**
   * Closes the drawer and unlocks the body scroll.
   */
  close() {
    if (!this.classList.contains('opened')) return;
    FocusLayer.pop(this, { returnFocus: this.activeElement });
    this.classList.remove('opened');
  }
}

customElements.define('drawer-simple', DrawerSimple);

// Open via delegation (fixes AJAX issues, works for any dynamically added CTAs)
document.addEventListener('click', (e) => {
  const cta = e.target.closest('.js-drawer-cta');
  if (!cta) return;

  e.preventDefault();
  const drawerId = cta.getAttribute('for');
  const drawer = document.getElementById(drawerId);

  if (drawer && typeof drawer.open === 'function') {
    drawer.open(cta);
  }
});
